import { Box, Typography, Button, Container, Grid, Paper, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import TimelineIcon from '@mui/icons-material/Timeline';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MessageIcon from '@mui/icons-material/Message';

export const LandingPage = () => {
  const features = [
    {
      icon: <TimelineIcon sx={{ fontSize: 48 }} />,
      title: 'CGM-Powered Insights',
      description: 'Real-time continuous glucose monitoring data drives personalized dietary recommendations.'
    },
    {
      icon: <RestaurantIcon sx={{ fontSize: 48 }} />,
      title: 'Stoplight Diet System',
      description: 'Easy-to-understand red/yellow/green food classifications tailored to your unique metabolism.'
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 48 }} />,
      title: 'AI-Generated Guidance',
      description: 'Intelligent system drafts personalized messages based on your meal logs and CGM patterns.'
    },
    {
      icon: <PersonIcon sx={{ fontSize: 48 }} />,
      title: 'Human-in-the-Loop',
      description: 'Licensed nutritionists review and approve all AI recommendations before delivery.'
    },
    {
      icon: <MessageIcon sx={{ fontSize: 48 }} />,
      title: 'Asynchronous Support',
      description: 'Get expert nutritionist guidance on your schedule, no appointments needed.'
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
      title: 'Evidence-Based Care',
      description: 'Every recommendation backed by your personal data: meals, CGM events, and context notes.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: 10
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              label="Human-in-the-Loop AI"
              sx={{
                mb: 3,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600
              }}
            />
            <Typography variant="h2" gutterBottom fontWeight={700}>
              TerraGlyc(TM) Nutrition System
            </Typography>
            <Typography variant="h5" paragraph sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
              Personalized stoplight diet powered by continuous glucose monitoring,
              AI intelligence, and expert nutritionist oversight
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to="/register/patient"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                Register as Patient
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/register/nutritionist"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'grey.100', bgcolor: 'rgba(255, 255, 255, 0.1)' }
                }}
              >
                Register as Nutritionist
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/register/admin"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'grey.100', bgcolor: 'rgba(255, 255, 255, 0.1)' }
                }}
              >
                Register as Admin
              </Button>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                component={RouterLink}
                to="/login"
                sx={{ color: 'white', textDecoration: 'underline' }}
              >
                Already have an account? Login
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight={600}>
          How It Works
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          paragraph
          sx={{ mb: 6 }}
        >
          Combining cutting-edge technology with personalized human care
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Ready to Start Your Journey?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Join hundreds of patients already achieving better glucose control
              with personalized dietary guidance
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/register/patient"
              sx={{ mt: 2 }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
